import React from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Box } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarIcon from "components/CalendarIcon";
import { RequiredLabel } from "components/RequiredLabel";

interface CommonDatePickerProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  rules?: RegisterOptions;
  disabled?: boolean;
  value: string | Dayjs | null;
  error?: boolean;
  helperText?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  format: string;
}

const CommonDatePicker = <T extends FieldValues>({
  name,
  control,
  label,
  rules,
  disabled = false,
  disablePast = false,
  disableFuture = false,
  format,
  error,
  helperText,
  value,
}: CommonDatePickerProps<T>) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: 3 }}>
        <Controller
          name={name}
          control={control}
          rules={{
            ...rules,
            deps: rules?.deps as Path<T> | Path<T>[] | undefined,
          }}
          render={({ field }) => (
            <DesktopDatePicker
              {...field}
              className="date-picker"
              label={
                <>
                  {label} {rules?.required && <RequiredLabel />}
                </>
              }
              format={format}
              value={value ?? dayjs()}
              disabled={disabled}
              disablePast={disablePast}
              disableFuture={disableFuture}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error,
                  helperText,
                },
              }}
              slots={{
                openPickerIcon: CalendarIcon,
              }}
            />
          )}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CommonDatePicker;
